import React, { Fragment, useEffect, useState } from "react";
import exploreEvents from "assets/images/common/others/exploreEvents.png";
import { ReactComponent as EditIcon } from "assets/images/common/svgs/edit-icon.svg";
import { ReactComponent as SearchIcon } from "assets/images/common/svgs/search.svg";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes.enum";
import useResourceLibrary from "../useHooks";
import { useSelector } from "react-redux";
import { ResourceLibFiltersDTO } from "utils/helpers/models/resource-library/resource-lib-filter.dto";
import { ROLES } from "utils/helpers/enums/roles.enum";
import { RESOURCE_TAGS } from "utils/helpers/enums/shared.enum";
import { useDebounce } from "hooks/useDebounce";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { ResourceDTO } from "utils/helpers/models/shared/resource.dto";
import DataNotFound from "components/particles/table/data-not-found";
import { sitePermissions } from "utils/helpers/enums/permissions.enum";
import useStore from "hooks/useStore";

const ResourceLibraryListing: React.FC = () => {
  const { hasPermission } = useStore();
  const [activeTab, setActiveTab] = useState<ROLES>();
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const { getResourceLibrary } = useResourceLibrary();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [filters, setFilters] = useState<ResourceLibFiltersDTO>(
    new ResourceLibFiltersDTO()
  );
  const debounceSearch = useDebounce(filters.title, 1000);

  const roles = [
    { title: "Attendee", value: ROLES.ATTENDEE },
    { title: "Host", value: ROLES.HOST },
    { title: "Ambassador", value: ROLES.AMBASSADOR },
  ];

  const getAllResources = (filterParams: any) => {
    const queryParams: any = { role: filters.role || ROLES.ATTENDEE };
    for (let key in filterParams) {
      if (filterParams[key] !== "") {
        queryParams[key] = filterParams[key];
      }
    }

    getResourceLibrary(setData, queryParams, setFilters);
  };

  useEffect(() => {
    if (!filters.role) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        role: ROLES.ATTENDEE,
      }));
    }

    getAllResources(filters);
  }, []);

  const handleFilterChange = (event: any) => {
    const { name, value } = event.target;
    const updatedFilters = { ...filters, [name]: value, page: 0, size: 10 };
    if (name === "role") {
      updatedFilters["tag"] = undefined;
      setActiveTab(value);
    }

    setFilters(updatedFilters);
  };

  const handleSearch = (event: any) => {
    const { key } = event;
    if (key === "Enter") {
      getAllResources(filters);
    }
  };

  const handleTabChange = (role: ROLES) => {
    setActiveTab(role);
    setFilters((prevFilters) => ({
      ...prevFilters,
      role: role,
    }));
    getAllResources({ ...filters, role, tag: undefined });
  };

  const handleClearFilters = () => {
    const model = new ResourceLibFiltersDTO();
    const currentRole = filters.role; // preserve current role
    setFilters({
      ...model,
      role: currentRole,
      tag: undefined,
    });

    getAllResources({
      ...model,
      role: currentRole,
      tag: undefined,
    });
  };

  return (
    <div className="pb-6">
      <h1 className="tex-2xl sm:text-[32px] text-primaryText font-bold mb-6">
        Resource Library
      </h1>
      <div className="flex flex-wrap justify-between items-center gap-5">
        {hasPermission(sitePermissions.resourceLibraryCreate) && (
          <button
            onClick={() => {
              navigate(siteRoutes.createResourceLibrary);
            }}
            className="px-4 py-2 text-sm bg-goldenOrangeBg text-white rounded-lg font-semibold"
          >
            Add New Resource
          </button>
        )}

        <div className="w-full sm:w-[388px] relative">
          <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-primaryText text-lg" />
          <input
            type="search"
            placeholder="Search by Title"
            name="title"
            value={filters.title}
            onChange={handleFilterChange}
            onKeyUp={handleSearch}
            className="w-full pl-10 p-2 border-[0.6px] border-borderLightGray rounded-full focus:outline-none bg-coolLightGrayBg"
          />
        </div>
      </div>
      <div className="bg-coolLightGrayBg border border-borderLightGray p-4 rounded-lg w-full my-6">
        <div className="w-full overflow-x-auto scrollbar-hidden">
          <div className="flex flex-wrap sm:flex-nowrap lg:justify-end items-center gap-3">
            <select
              name="tag"
              value={filters.tag || ""}
              onChange={handleFilterChange}
              className="px-3 border border-lightGrayBorder text-primaryText outline-none rounded-lg text-sm bg-white h-[46px] w-full sm:w-auto min-w-[140px]"
            >
              <option value="">Tags</option>
              <option value="Guides">GUIDES</option>
              <option value="Tips">TIPS</option>
              <option value="Instructions">INSTRUCTIONS</option>
              <option value="More Instructions">MORE INSTRUCTIONS</option>
            </select>

            {/* Apply Filters Button */}
            <button
              onClick={() => getAllResources(filters)}
              className="px-4 py-2 bg-darkTealBlue text-white rounded-lg text-sm h-[46px] w-full md:w-auto min-w-[140px]"
            >
              Apply Filters
            </button>

            {/* Clear Filters Button */}
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-400 text-white rounded-lg text-sm h-[46px] w-full md:w-auto min-w-[140px]"
            >
              Clear Filter
            </button>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-3 justify-start mb-6">
        {roles.map((role) => (
          <button
            key={role.value}
            type="button"
            className={`px-4 py-2 text-xs rounded-md transition-colors duration-300 ${filters.role === role.value
                ? "bg-vibrantBlueBg text-white"
                : "bg-warmOffWhiteBg text-mediumGrayText font-bold"
              }`}
            onClick={() => handleTabChange(role.value)}
          >
            {role.title}
          </button>
        ))}
      </div>

      {/* Filtered Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
        {data.map((exp: ResourceDTO, index: number) => (
          <div
            key={index}
            className="p-3 bg-white rounded-xl border border-borderColor"
          >
            <div className="relative">
              <img
                src={concatPathWithBackendUrl(exp.thumbnail?.fileDownloadUri)}
                alt={exp.title}
                className="w-full h-32 object-cover rounded-xl"
              />
              {/* <div className="absolute top-2 left-2 bg-white text-center p-2 rounded-md">
                <span className="block text-sm font-bold text-forestText">
                  {exp.date}
                </span>
                <span className="block text-xs text-forestText">
                  {exp.month}
                </span>
              </div> */}
            </div>
            <div className="py-2">
              <h3 className="text-lg font-semibold text-primaryText">
                {exp.title}
              </h3>

              <div className="flex justify-between items-center mt-2">
                <div className="flex flex-wrap gap-2">
                  {exp.tags.map((tagss: any, index: number) => (
                    <span
                      key={index}
                      className="bg-forestBg text-[13px] rounded-md px-2 py-1 text-forestText"
                    >
                      {tagss}
                    </span>
                  ))}
                </div>
                <Fragment>
                  {hasPermission(sitePermissions.resourceLibraryUpdate) ? (
                    <div
                      onClick={() => {
                        navigate(
                          `${siteRoutes.editResourceLibrary}?id=${exp.id}`
                        );
                      }}
                      className="bg-goldenOrangeBg p-2.5 rounded-lg cursor-pointer"
                    >
                      <EditIcon />
                    </div>
                  ) : (
                    <div className="bg-goldenOrangeBg p-2.5 rounded-lg cursor-not-allowed">
                      <EditIcon />
                    </div>
                  )}
                </Fragment>
              </div>
            </div>
          </div>
        ))}
      </div>
      <DataNotFound show={!isLoading && !data.length} />
    </div>
  );
};

export default ResourceLibraryListing;
